// Chemin: C:\gestion-copro\src\hooks\useAideContextuelle.ts
// Session 136 : Hook réutilisable — Aide contextuelle 3 niveaux
// Factorise la logique commune à toutes les pages VIGIE (et CS à terme)
// Niveau 1 : Accueil riche (première visite)
// Niveau 2 : Bandeau compact permanent (après fermeture)
// Niveau 3 : Tooltips (gérés individuellement par chaque page)
// Persistance via Firestore userPreferences/{userId}.aideVue.{moduleKey}

import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

interface UseAideContextuelleOptions {
  /** Clé unique du module (ex: 'calendrier', 'courriers', 'contacts') */
  moduleKey: string;
}

interface UseAideContextuelleReturn {
  /** true une fois la préférence chargée depuis Firestore */
  aideChargee: boolean;
  /** true si l'accueil riche a déjà été vu (= afficher le compact) */
  aideAccueilVue: boolean;
  /** true si l'utilisateur a cliqué "En savoir plus" depuis le compact */
  afficherAideComplete: boolean;
  /** Fermer l'accueil riche et persister dans Firestore */
  fermerAideAccueil: () => Promise<void>;
  /** Réouvrir l'accueil riche depuis le bandeau compact */
  ouvrirAideComplete: () => void;
}

export function useAideContextuelle({ moduleKey }: UseAideContextuelleOptions): UseAideContextuelleReturn {
  const { userProfile } = useAuth();

  // true = déjà vue (afficher compact), false = première visite (afficher accueil riche)
  const [aideAccueilVue, setAideAccueilVue] = useState(true);
  const [aideChargee, setAideChargee] = useState(false);
  const [afficherAideComplete, setAfficherAideComplete] = useState(false);

  // Charger la préférence depuis Firestore
  useEffect(() => {
    const chargerPreference = async () => {
      if (!userProfile?.id) return;
      try {
        const prefRef = doc(db, 'userPreferences', userProfile.id);
        const prefSnap = await getDoc(prefRef);
        if (prefSnap.exists()) {
          const data = prefSnap.data();
          const dejaVu = data?.aideVue?.[moduleKey] === true;
          setAideAccueilVue(dejaVu);
        } else {
          // Pas de préférences → première visite
          setAideAccueilVue(false);
        }
      } catch (error) {
        console.warn(`[Aide ${moduleKey}] Erreur chargement préférence:`, error);
        // En cas d'erreur, afficher l'aide (mieux vaut trop que pas assez)
        setAideAccueilVue(false);
      } finally {
        setAideChargee(true);
      }
    };
    chargerPreference();
  }, [userProfile?.id, moduleKey]);

  // Fermer l'accueil riche et persister
  const fermerAideAccueil = useCallback(async () => {
    setAideAccueilVue(true);
    setAfficherAideComplete(false);
    if (userProfile?.id) {
      try {
        const prefRef = doc(db, 'userPreferences', userProfile.id);
        await setDoc(prefRef, { aideVue: { [moduleKey]: true } }, { merge: true });
      } catch (error) {
        console.warn(`[Aide ${moduleKey}] Erreur sauvegarde préférence:`, error);
      }
    }
  }, [userProfile?.id, moduleKey]);

  // Réouvrir depuis le compact
  const ouvrirAideComplete = useCallback(() => {
    setAfficherAideComplete(true);
  }, []);

  return {
    aideChargee,
    aideAccueilVue,
    afficherAideComplete,
    fermerAideAccueil,
    ouvrirAideComplete,
  };
}
